import { isPwa, Sentry } from '@ghl/clientportal-core/init'
import postscribe from 'postscribe'

export function sanitizeJavaScriptCode(jsCode: string): string {
  /**
   * Remove HTML tags and leading/trailing white spaces
   * We currently don't have a way to validate customJS, for now add a try catch block to avoid errors from customJS to propagate to sentry
   * Make sure to console it to give an idea of what went wrong to customer
   */
  return `try {
    ${jsCode.replace(/<\/?[^>]+(>|$)/g, '').trim()}
  } catch(error) {
    console.error("Custom JS execution error:", error);
  }`
}
export const initiateCustomTags = (data: any): void => {
  if (isPwa()) {
    return
  }

  try {
    // Set Sentry tag to identify custom code usage
    Sentry.setTag(
      'customCode',
      data?.customJs || data?.headerCode || data?.footerCode ? 'true' : 'false'
    )

    addCustomCSSTag(data)

    // Validate and add custom JS with error handling
    const jsCode = sanitizeJavaScriptCode(data?.customJs || '')
    let isValid = true
    try {
      new Function(jsCode)
    } catch (e) {
      isValid = false
      console.error('Custom JS is invalid:', e)
    }

    if (isValid) {
      addCustomJsTag(data)
    }

    if (data?.headerCode) {
      // Configure postscribe to not use fetch for scripts
      postscribe(`#clientportal-siteCustomHeader`, data.headerCode, {
        beforeWriteToken: token => {
          // Prevent postscribe from fetching external scripts
          if (token.tagName === 'script' && token.attrs.src) {
            return false // Let browser handle script loading naturally
          }
          return token
        },
        error: err => {
          console.error('Error posting custom header:', err)
        },
      })
    }
    if (data?.footerCode) {
      postscribe(`#clientportal-siteCustomFooter`, data.footerCode, {
        beforeWriteToken: token => {
          // Prevent postscribe from fetching external scripts
          if (token.tagName === 'script' && token.attrs.src) {
            return false // Let browser handle script loading naturally
          }
          return token
        },
        error: err => {
          console.error('Error posting custom footer:', err)
        },
      })
    }
  } catch (error) {
    console.error(`Error initializing custom tags: ${error}`)
  }
}
export const addCustomCSSTag = (data: any): void => {
  const customCssCode = document.createElement('style')
  customCssCode.setAttribute('type', 'text/css')
  customCssCode.setAttribute('vmid', 'clientportal-site-custom-css')
  ;(customCssCode.innerHTML = data.customCss),
    document.head.appendChild(customCssCode)
}
export const addCustomJsTag = (data: any): void => {
  const customScript = document.createElement('script')
  customScript.setAttribute('type', 'text/javascript')
  customScript.setAttribute('vmid', 'clientportal-site-custom-js')
  customScript.innerHTML = sanitizeJavaScriptCode(data?.customJs || '')
  document.head.appendChild(customScript)
}
export const initiateCustomTagsCommunities = async (
  data: any
): Promise<boolean> => {
  if (isPwa()) {
    return false
  }

  if (data?.customCode === 'false') {
    return false
  }

  try {
    // Set Sentry tag to identify custom code usage
    Sentry.setTag(
      'customCode',
      data?.customJs || data?.headerCode || data?.footerCode ? 'true' : 'false'
    )

    if (data?.liveMode === true || data?.customCode === 'true') {
      addCustomCSSTagCommunities(data)

      // Validate and add custom JS with error handling
      const jsCode = sanitizeJavaScriptCode(data?.customJs || '')
      let isValid = true
      try {
        new Function(jsCode)
      } catch (e) {
        isValid = false
        console.error('Custom JS is invalid:', e)
      }

      if (isValid) {
        addCustomJsTagCommunities(data)
      }

      if (data?.headerCode) {
        postscribe(`#communities-siteCustomHeader`, data.headerCode, {
          error: err => {
            console.error('Error posting communities custom header:', err)
          },
        })
      }
      if (data?.footerCode) {
        postscribe(`#communities-siteCustomFooter`, data.footerCode, {
          error: err => {
            console.error('Error posting communities custom footer:', err)
          },
        })
      }
      return true
    }
    return false
  } catch (error) {
    console.error(`Error initializing communities custom tags: ${error}`)
    return false
  }
}
export const addCustomCSSTagCommunities = (data: any): void => {
  const customCssCode = document.createElement('style')
  customCssCode.setAttribute('type', 'text/css')
  customCssCode.setAttribute('vmid', 'communities-site-custom-css')
  ;(customCssCode.innerHTML = data?.customCss),
    document.head.appendChild(customCssCode)
}
export const addCustomJsTagCommunities = (data: any): void => {
  const customScript = document.createElement('script')
  customScript.setAttribute('type', 'text/javascript')
  customScript.setAttribute('vmid', 'communities-site-custom-js')
  customScript.innerHTML = sanitizeJavaScriptCode(data?.customJs || '')
  document.head.appendChild(customScript)
}
